import { useEffect, useState, useSyncExternalStore } from 'react'

import { useMe } from '@/api/queries'

import { canDo, parseTier, type Capability, type StaffTier } from './tier'
import { getViewAs, isLive, subscribeViewAs, type ViewAsState } from './view-as'

/** The live view-as session, or null. Re-renders when it starts, ends or gains a grant. */
export function useViewAs(): ViewAsState | null {
  return useSyncExternalStore(subscribeViewAs, getViewAs, getViewAs)
}

/** True while a read-only view-as session is live: write controls must not be drawn. */
export function useReadOnly(): boolean {
  const session = useViewAs()
  const now = useNow(session === null ? 0 : 5_000)
  return session !== null && isLive(session.expiresAt, now)
}

/** `Date.now()` re-read every `intervalMs` (0 = never). */
export function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (intervalMs <= 0) return
    const id = window.setInterval(() => {
      setNow(Date.now())
    }, intervalMs)
    return () => {
      window.clearInterval(id)
    }
  }, [intervalMs])
  return now
}

export interface StaffIdentity {
  tier: StaffTier | null
  /** False when the session lacks the second factor (every staff endpoint answers mfa_required). */
  mfa: boolean
  loading: boolean
  can: (capability: Capability) => boolean
}

/**
 * The caller's own staff tier. While viewing as an account `/api/me` answers as that account's
 * ADMIN, so the tier comes from the one remembered when the session started.
 */
export function useStaff(): StaffIdentity {
  const me = useMe()
  const session = useViewAs()
  const viewing = session !== null && isLive(session.expiresAt)
  const tier = viewing ? parseTier(session.staffTier) : parseTier(me.data?.staff_tier)
  return {
    tier,
    mfa: viewing || me.data?.staff_mfa !== false,
    loading: !viewing && me.isPending,
    can: (capability) => canDo(tier, capability),
  }
}
